// src/pages/Login.jsx
// Renderiza la interfaz de inicio de sesión

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/loginContext";
import "../styles/loginAutentic.css";
import { FcGoogle } from "react-icons/fc";
import banner from "../assets/banner.png";
import { sendPasswordReset } from "../firebase";
import Swal from "sweetalert2";
import bg1 from "../styles/imgsbg/login-bg.jpg";
import bg2 from "../styles/imgsbg/login-bg (2).jpg";
import bg3 from "../styles/imgsbg/login-bg (3).jpg";
import bg4 from "../styles/imgsbg/login-bg (4).jpg";

const LOGIN_BACKGROUNDS = [bg1, bg2, bg3, bg4];

function getAuthMessage(error) {
  switch (error?.code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Usuario o contraseña incorrectos.";
    case "auth/invalid-email":
      return "Ingresa un correo válido.";
    case "auth/user-disabled":
      return "Esta cuenta se encuentra deshabilitada.";
    case "auth/too-many-requests":
      return "Demasiados intentos. Espera unos minutos antes de volver a intentarlo.";
    case "auth/network-request-failed":
      return "Revisa tu conexión a internet e inténtalo de nuevo.";
    case "auth/popup-closed-by-user":
      return "Inicio de sesión cancelado. Intenta de nuevo.";
    case "auth/popup-blocked":
      return "El navegador bloqueó la ventana. Permite popups e inténtalo de nuevo.";
    default:
      return "No pudimos completar la operación. Intenta de nuevo.";
  }
}

export default function Login() {
  const navigate = useNavigate();
  const { manualLogin, loginWithGoogle, user, loading, role, authError } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberSession, setRememberSession] = useState(false);
  const [formLoading, setFormLoading] = useState(null);
  const [backgroundImage] = useState(
    () => LOGIN_BACKGROUNDS[Math.floor(Math.random() * LOGIN_BACKGROUNDS.length)]
  );

  // Redirección cuando ya hay usuario autenticado
  useEffect(() => {
    if (!user) return;

    console.log("[LOGIN] Usuario autenticado:", user.email, "role:", role);

    // Ahora TODOS (admin y manager) van primero al dashboard
    // "/" redirige a la pantalla de inicio según el rol.
    navigate("/", { replace: true });
  }, [user, role, navigate]);

  useEffect(() => {
    if (!authError) return;
    Swal.fire({ icon: "error", title: "No se pudo iniciar sesión", text: authError });
  }, [authError]);


  // Login manual (email/contraseña)
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setFormLoading("login");
    try {
      const normalizedEmail = (email || "").trim().toLowerCase();
      await manualLogin(normalizedEmail, password, rememberSession);
      // el useEffect hará la redirección
    } catch (err) {
      console.error("[LOGIN] Error en manualLogin:", err);
    } finally {
      setFormLoading(null);
    }
  };

  // Login con Google
  const handleGoogle = async () => {
    setFormLoading("google");
    try {
      await loginWithGoogle(rememberSession);
      // onAuthStateChanged + useEffect hacen el resto
    } catch (err) {
      console.error("[LOGIN] Error en loginWithGoogle:", err);
      const code = err?.code || "";
      const message = (err?.message || "").includes("Correo no registrado")
        ? "Correo no registrado"
        : getAuthMessage(err);
      Swal.fire({ icon: code === "auth/popup-closed-by-user" ? "info" : "error", title: "Inicio de sesión", text: message });
    } finally {
      setFormLoading(null);
    }
  };

  const handlePasswordReset = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      Swal.fire({ icon: "warning", title: "Ingresa tu correo", text: "Escribe tu usuario para recuperar la contraseña." });
      return;
    }

    setFormLoading("reset");
    try {
      await sendPasswordReset(normalizedEmail);
      Swal.fire({ icon: "success", title: "Revisa tu correo", text: "Te enviamos un enlace para restablecer tu contraseña." });
    } catch (err) {
      console.error("[LOGIN] Error en recuperación de contraseña:", err);
      Swal.fire({ icon: "error", title: "No pudimos enviar el enlace", text: "Verifica tu correo e intenta de nuevo." });
    } finally {
      setFormLoading(null);
    }
  };

  const isBusy = loading || Boolean(formLoading);

  return (
    <main className="auth-page" style={{ backgroundImage: `url("${backgroundImage}")` }}>
      <div className="auth-card-wrapper">
        <div className="auth-card">
          <img className="auth-brand" src={banner} alt="Centro Guaman Poma de Ayala" />
          <h1>Portal del Trabajador</h1>
          <div className="hint">Bienvenido. Ingresa con tu cuenta institucional.</div>

          <form className="auth-form" onSubmit={handleSubmit} autoComplete="on">
            <div className="auth-field">
              <input
                id="email"
                type="email"
                placeholder="Usuario"
                aria-label="Usuario"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isBusy}
                autoComplete="email"
                required
              />
            </div>

            <div className="auth-field">
              <input
                id="password"
                type="password"
                placeholder="Contraseña"
                aria-label="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isBusy}
                autoComplete="current-password"
                required
              />
            </div>

            <div className="auth-options">
              <label className="remember-session">
                <input
                  type="checkbox"
                  checked={rememberSession}
                  onChange={(e) => setRememberSession(e.target.checked)}
                  disabled={isBusy}
                />
                Recordar sesión
              </label>
              <button
                type="button"
                className="forgot-password"
                onClick={handlePasswordReset}
                disabled={isBusy}
              >
                {formLoading === "reset" ? "Enviando enlace..." : "¿Olvidaste tu contraseña?"}
              </button>
            </div>

            <button type="submit" className="auth-btn" disabled={isBusy}>
              {formLoading === "login" ? "Ingresando..." : "Iniciar sesión"}
            </button>

            <div className="divider">o continuar con</div>

            <button
              type="button"
              className="google-btn"
              onClick={handleGoogle}
              disabled={isBusy}
            >
              <FcGoogle className="google-icon" />
              {formLoading === "google" ? "Conectando con Google..." : "Iniciar sesión con Google"}
            </button>

          </form>
        </div>
      </div>
    </main>
  );
}
